import { useCallback, useEffect, useState } from "react";
import { Pencil } from "lucide-react";

import { businessApi } from "../../../../services/api";
import {
  Button, ConfirmDialog, ErrorState, Field, Input, Notice, SkeletonRows, Surface, Textarea, cn, focusRing, plainError,
} from "../../../../components/ui";

// Report 58 (APP-HR-138, APP-HR-140): the business's standard week and the paid hours of a standard day. The server
// keeps them as dated versions; this panel shows what is in force, what is scheduled, and lets an HR settings manager
// set a new version from a date. Each employee's own weekly schedule always decides their work days.

export const WEEKDAYS = [
  { day: 1, short: "Mon", name: "Monday" }, { day: 2, short: "Tue", name: "Tuesday" }, { day: 3, short: "Wed", name: "Wednesday" },
  { day: 4, short: "Thu", name: "Thursday" }, { day: 5, short: "Fri", name: "Friday" }, { day: 6, short: "Sat", name: "Saturday" },
  { day: 7, short: "Sun", name: "Sunday" },
];
const longDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
const fromText = (row) => (row.fromTheStart ? "Since the business started using Apparify" : `From ${longDate(row.effectiveFrom)}`);
const hoursText = (value) => `${Number(value)} paid hour${Number(value) === 1 ? "" : "s"}`;
const sameDays = (a = [], b = []) => a.length === b.length && a.every((day) => b.includes(day));
export const weekdayNames = (days = []) => WEEKDAYS.filter((item) => days.includes(item.day)).map((item) => item.name);

/** Field checks before anything is sent; the server checks the same and more. */
export function workStandardErrors(form, { minPaidHours = 1, maxPaidHours = 8 } = {}) {
  const errors = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.effectiveFrom || ""))) errors.effectiveFrom = "Choose the date the new standard week applies from.";
  if (!form.weekdays.length) errors.weekdays = "Tick at least one work day.";
  else if (form.weekdays.length > 6) errors.weekdays = "Leave at least one day unticked: every employee needs a rest day. A shop open every day gives each employee their own rest day in their schedule.";
  const hours = Number(form.paidHoursPerDay);
  if (!Number.isFinite(hours) || hours < minPaidHours || hours > maxPaidHours || Math.round(hours * 4) !== hours * 4) {
    errors.paidHoursPerDay = `Enter ${minPaidHours} to ${maxPaidHours} hours, in quarter hours (for example 7.5).`;
  }
  const reason = String(form.reason || "").trim();
  if (reason.length < 5) errors.reason = "Say why it changes (at least 5 characters).";
  else if (reason.length > 500) errors.reason = "Keep it to 500 characters.";
  return errors;
}

function WeekStrip({ weekdays }) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Standard week">
      {WEEKDAYS.map((item) => {
        const work = weekdays.includes(item.day);
        return (
          <li key={item.day}
            className={cn("min-w-[3.25rem] rounded-control border px-2 py-1 text-center text-body-sm",
              work ? "border-default bg-surface text-foreground" : "border-subtle bg-surface-sunken text-tertiary")}>
            <span className="block font-medium">{item.short}</span>
            <span className="block text-meta">{work ? "Work" : "Rest"}</span>
          </li>
        );
      })}
    </ul>
  );
}

function StandardFacts({ standard }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
      <dt className="text-meta text-secondary">Standard week</dt>
      <dd className="text-body text-foreground">
        <WeekStrip weekdays={standard.weekdays} />
        <span className="mt-1 block text-body-sm text-secondary">{standard.weekdaysText} · {standard.workDaysPerWeek} work days, rest on {weekdayNames(standard.restWeekdays).join(" and ") || "no day"}</span>
      </dd>
      <dt className="text-meta text-secondary">Paid hours in a standard day</dt>
      <dd className="text-body text-foreground">{hoursText(standard.paidHoursPerDay)}</dd>
      <dt className="text-meta text-secondary">Applies</dt>
      <dd className="text-body text-foreground">{fromText(standard)}</dd>
    </dl>
  );
}

function ChangeForm({ data, onCancel, onSaved }) {
  const current = data.current;
  const [form, setForm] = useState(() => ({
    effectiveFrom: "", weekdays: [...(current?.weekdays || [1, 2, 3, 4, 5])], paidHoursPerDay: String(current?.paidHoursPerDay ?? 8), reason: "",
  }));
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const set = (key) => (event) => { setForm((now) => ({ ...now, [key]: event.target.value })); setErrors((now) => ({ ...now, [key]: undefined })); };
  const toggle = (day) => {
    setForm((now) => ({ ...now, weekdays: now.weekdays.includes(day) ? now.weekdays.filter((item) => item !== day) : [...now.weekdays, day].sort((a, b) => a - b) }));
    setErrors((now) => ({ ...now, weekdays: undefined }));
  };
  const check = () => {
    const found = workStandardErrors(form, data.limits);
    setErrors(found);
    if (!Object.keys(found).length) setReview(true);
  };
  const save = async () => {
    const response = await businessApi.recordHRWorkStandard({
      effectiveFrom: form.effectiveFrom, weekdays: form.weekdays, paidHoursPerDay: Number(form.paidHoursPerDay), reason: form.reason.trim(),
    });
    setReview(false);
    onSaved(response.standard);
  };
  const rest = WEEKDAYS.filter((item) => !form.weekdays.includes(item.day)).map((item) => item.name);

  return (
    <div className="mt-4 space-y-4 border-t border-subtle pt-4">
      <h3 className="text-panel-title text-foreground">Change the standard week</h3>
      <fieldset aria-describedby="standard-week-hint">
        <legend className="text-label text-secondary">Work days <span className="text-danger">*</span></legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {WEEKDAYS.map((item) => {
            const checked = form.weekdays.includes(item.day);
            return (
              <label key={item.day}
                className={cn("inline-flex min-h-touch cursor-pointer items-center gap-2 rounded-control border px-3 text-body-sm md:min-h-0 md:py-1.5",
                  checked ? "border-accent bg-accent-subtle text-accent" : "border-default bg-surface text-foreground", focusRing)}>
                <input type="checkbox" className="h-4 w-4" checked={checked} onChange={() => toggle(item.day)} aria-label={item.name} />
                {item.short}
              </label>
            );
          })}
        </div>
        <p id="standard-week-hint" className={cn("mt-1 text-meta", errors.weekdays ? "text-danger" : "text-tertiary")} role={errors.weekdays ? "alert" : undefined}>
          {errors.weekdays || `${form.weekdays.length} work day${form.weekdays.length === 1 ? "" : "s"}; rest on ${rest.join(", ") || "no day"}. Sunday can be a work day.`}
        </p>
      </fieldset>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Paid hours in a standard day" required error={errors.paidHoursPerDay} hint={`${data.limits?.minPaidHours ?? 1} to ${data.limits?.maxPaidHours ?? 8} hours. Time beyond eight hours a day is overtime and is paid separately.`}>
          <Input type="number" inputMode="decimal" min={data.limits?.minPaidHours ?? 1} max={data.limits?.maxPaidHours ?? 8} step="0.25" value={form.paidHoursPerDay} onChange={set("paidHoursPerDay")} />
        </Field>
        <Field label="Applies from" required error={errors.effectiveFrom} hint="New employees' schedules and pay set from this day use it. A payroll already with Finance or issued is never changed.">
          <Input type="date" value={form.effectiveFrom} onChange={set("effectiveFrom")} />
        </Field>
      </div>
      <Field label="Reason" required error={errors.reason} hint="For example: the shop now opens on Sundays. 5 to 500 characters.">
        <Textarea rows={2} maxLength={500} value={form.reason} onChange={set("reason")} />
      </Field>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={check}>Review change</Button>
      </div>
      {review ? (
        <ConfirmDialog
          size="md"
          title={`Change the standard week from ${longDate(form.effectiveFrom)}?`}
          facts={[
            { label: "Work days", value: sameDays(current?.weekdays, form.weekdays) ? `${current.weekdaysText} (unchanged)` : `${current?.weekdaysText || "-"} → ${weekdayNames(form.weekdays).join(", ")}` },
            { label: "Paid hours in a standard day", value: current && Number(current.paidHoursPerDay) === Number(form.paidHoursPerDay) ? `${hoursText(form.paidHoursPerDay)} (unchanged)` : `${current ? hoursText(current.paidHoursPerDay) : "-"} → ${hoursText(form.paidHoursPerDay)}` },
            { label: "Reason", value: form.reason.trim() },
          ]}
          consequence="New employees start from this week, and pay set from now on is worked out with it. Employees who already have a schedule keep their own work days and rest days, and pay already set is not changed."
          confirmLabel="Save standard week"
          cancelLabel="Back to editing"
          failureTitle="Not saved"
          onConfirm={save}
          onClose={() => setReview(false)}
        >
          <p className="text-body-sm text-secondary">Payroll also uses this week for anyone who has no schedule of their own, and for an hourly employee&apos;s paid leave on a day with no shift.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

export default function WorkStandardPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await businessApi.getHRWorkStandard();
      setData(response.standard);
      setError("");
    } catch (err) {
      setError(plainError(err, "The standard week couldn't be loaded."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (next, verb) => {
    setData(next);
    setEditing(false);
    const stale = next.recalculate || [];
    setResult(`${verb}.${stale.length ? ` Recalculate ${stale.map((row) => row.name).join(", ")} so ${stale.length === 1 ? "it uses" : "they use"} it.` : ""}`);
  };

  if (error && !data) return <ErrorState title="The standard week couldn't be loaded" description={error} onRetry={load} />;
  if (!data) return <SkeletonRows rows={3} columns={2} label="Loading the standard week" />;

  return (
    <Surface padded as="section" aria-label="Standard week">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h2 className="text-section-title text-foreground">Standard week</h2>
        {data.canManage && !editing ? <Button size="sm" icon={Pencil} onClick={() => { setEditing(true); setResult(null); }}>Change standard week</Button> : null}
      </div>
      <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">
        The work days a new employee&apos;s weekly schedule starts from, and the paid hours of an ordinary day. It is a
        starting point: each employee&apos;s own schedule in Shifts decides their real work and rest days.
      </p>
      <div className="mt-4"><StandardFacts standard={data.current} /></div>

      {(data.scheduled || []).map((change) => (
        <div key={change.id} className="mt-4 rounded-panel border border-default p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
            <h3 className="text-panel-title text-foreground">Change starting {longDate(change.effectiveFrom)}</h3>
            {data.canManage ? <Button size="sm" variant="quiet" onClick={() => setWithdrawing(change)}>Withdraw change</Button> : null}
          </div>
          <div className="mt-3"><StandardFacts standard={change} /></div>
        </div>
      ))}

      <details className="mt-4 border-t border-subtle pt-3">
        <summary className={cn("cursor-pointer rounded-control text-body-sm font-medium text-foreground max-md:min-h-touch max-md:py-2", focusRing)}>What these two settings are used for</summary>
        <ul className="mt-2 max-w-[75ch] list-disc space-y-1 pl-5 text-body-sm text-secondary">
          <li><span className="font-medium text-foreground">Work days</span>: the first schedule a new employee gets (you can change it for each person), and the days payroll counts for someone who has no schedule at all. Pay set for a fixed salary is divided by these days a week.</li>
          <li><span className="font-medium text-foreground">Paid hours in a standard day</span>: turns a daily rate into an hourly rate when pay is set, and is a paid leave day for an hourly employee on a day with no shift. It is not a shift length: each shift sets its own times and break.</li>
          <li>It never decides overtime. Normal hours of work are at most eight a day under the labour rules, whatever is set here.</li>
        </ul>
      </details>

      {!data.canManage ? <p className="mt-3 text-body-sm text-secondary">Only someone who manages HR settings can change the standard week.</p> : null}
      {editing ? <ChangeForm data={data} onCancel={() => setEditing(false)} onSaved={(next) => saved(next, "Standard week saved")} /> : null}
      {result ? <div className="mt-4"><Notice tone="success" role="status">{result}</Notice></div> : null}

      {(data.history || []).length > 1 ? (
        <details className="mt-4 border-t border-subtle pt-3">
          <summary className={cn("cursor-pointer rounded-control text-body-sm font-medium text-foreground max-md:min-h-touch max-md:py-2", focusRing)}>Earlier and withdrawn versions</summary>
          <ul className="mt-2 divide-y divide-subtle">
            {data.history.map((row) => (
              <li key={row.id} className="py-2 text-body-sm">
                <span className="font-medium text-foreground">{fromText(row)}</span>
                <span className="text-secondary"> · {row.weekdaysText} · {hoursText(row.paidHoursPerDay)}</span>
                {row.withdrawnAt ? <span className="text-secondary"> · withdrawn</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {withdrawing ? (
        <ConfirmDialog
          tone="danger"
          title={`Withdraw the change starting ${longDate(withdrawing.effectiveFrom)}?`}
          consequence="The standard week in force now keeps applying from that date. The change stays in the history as withdrawn."
          confirmLabel="Withdraw change"
          cancelLabel="Keep change"
          failureTitle="Not withdrawn"
          onConfirm={async () => {
            const response = await businessApi.withdrawHRWorkStandard(withdrawing.id);
            setWithdrawing(null);
            saved(response.standard, "Change withdrawn");
          }}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
    </Surface>
  );
}
